import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Alert,
  Typography,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  WorkflowStage,
  setSaltSelection,
  confirmSaltSelection,
  setStageValidation,
} from '../store/slices/workflowSlice';
import WorkflowLayout from '../components/workflow/WorkflowLayout';
import SaltLibraryReview from '../components/salt-library/SaltLibraryReview';
import CustomSaltCreation from '../components/salt-library/CustomSaltCreation';
import SelectionAnalysisPanel from '../components/salt-library/SelectionAnalysisPanel';
import SaltCombinationsPanel from '../components/salt-library/SaltCombinationsPanel';
import type { SaltCombination } from '../utils/saltCombinationsStorage';

// Salt compatibility matrix - based on hydroponic research
const saltCompatibilityMatrix: { [key: string]: { [key: string]: 'beneficial' | 'neutral' | 'caution' | 'incompatible' } } = {
  'calcium-nitrate': {
    'potassium-sulfate': 'beneficial',
    'magnesium-sulfate': 'beneficial',
    'monopotassium-phosphate': 'neutral',
    'potassium-nitrate': 'neutral',
    'iron-chelate': 'neutral',
    'calcium-chloride': 'caution',
    'calcium-sulfate': 'caution'
  },
  'potassium-nitrate': {
    'calcium-nitrate': 'neutral',
    'magnesium-sulfate': 'beneficial',
    'monopotassium-phosphate': 'beneficial',
    'potassium-sulfate': 'neutral',
    'iron-chelate': 'neutral'
  },
  'monopotassium-phosphate': {
    'calcium-nitrate': 'neutral',
    'potassium-nitrate': 'beneficial',
    'magnesium-sulfate': 'neutral',
    'iron-chelate': 'neutral'
  },
  'magnesium-sulfate': {
    'calcium-nitrate': 'beneficial',
    'potassium-nitrate': 'beneficial',
    'potassium-sulfate': 'beneficial',
    'iron-chelate': 'neutral'
  },
  'iron-chelate': {
    'calcium-nitrate': 'neutral',
    'potassium-nitrate': 'neutral',
    'magnesium-sulfate': 'neutral',
    'monopotassium-phosphate': 'neutral'
  }
};

// Standard nutrient salts library
const standardSalts = [
  {
    id: 'calcium-nitrate',
    name: 'Calcium Nitrate',
    formula: 'Ca(NO₃)₂·4H₂O',
    elements: [
      { symbol: 'Ca', percentage: 19.0 },
      { symbol: 'N', percentage: 15.5 }
    ],
    solubility: 1290, // g/L at 20°C
    cost: 0.85, // $/kg
    availability: 'common',
    notes: 'Primary calcium and nitrogen source. Highly soluble.'
  },
  {
    id: 'potassium-nitrate',
    name: 'Potassium Nitrate',
    formula: 'KNO₃',
    elements: [
      { symbol: 'K', percentage: 38.7 },
      { symbol: 'N', percentage: 13.8 }
    ],
    solubility: 316,
    cost: 1.20,
    availability: 'common',
    notes: 'Excellent K and N source. Clean burning.'
  },
  {
    id: 'monopotassium-phosphate',
    name: 'Monopotassium Phosphate (MKP)',
    formula: 'KH₂PO₄',
    elements: [
      { symbol: 'K', percentage: 28.7 },
      { symbol: 'P', percentage: 22.8 }
    ],
    solubility: 230,
    cost: 2.50,
    availability: 'common',
    notes: 'Primary phosphorus source. pH buffering.'
  },
  {
    id: 'magnesium-sulfate',
    name: 'Magnesium Sulfate (Epsom Salt)',
    formula: 'MgSO₄·7H₂O',
    elements: [
      { symbol: 'Mg', percentage: 9.9 },
      { symbol: 'S', percentage: 13.0 }
    ],
    solubility: 710,
    cost: 0.60,
    availability: 'common',
    notes: 'Primary magnesium and sulfur source.'
  },
  {
    id: 'potassium-sulfate',
    name: 'Potassium Sulfate',
    formula: 'K₂SO₄',
    elements: [
      { symbol: 'K', percentage: 44.9 },
      { symbol: 'S', percentage: 18.4 }
    ],
    solubility: 120,
    cost: 1.80,
    availability: 'common',
    notes: 'Chloride-free potassium source.'
  },
  {
    id: 'iron-chelate',
    name: 'Iron Chelate (Fe-DTPA)',
    formula: 'Fe-DTPA',
    elements: [
      { symbol: 'Fe', percentage: 10.0 }
    ],
    solubility: 1000,
    cost: 8.50,
    availability: 'specialty',
    notes: 'Stable iron source. pH range 4-7.'
  },
  {
    id: 'micronutrient-mix',
    name: 'Micronutrient Mix',
    formula: 'Mixed',
    elements: [
      { symbol: 'Mn', percentage: 2.0 },
      { symbol: 'Zn', percentage: 1.5 },
      { symbol: 'Cu', percentage: 0.5 },
      { symbol: 'B', percentage: 1.0 },
      { symbol: 'Mo', percentage: 0.1 }
    ],
    solubility: 800,
    cost: 12.00,
    availability: 'specialty',
    notes: 'Complete micronutrient blend.'
  }
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`salt-tabpanel-${index}`}
      aria-labelledby={`salt-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `salt-tab-${index}`,
    'aria-controls': `salt-tabpanel-${index}`,
  };
}

const SaltLibraryWorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedSalts, setSelectedSalts] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeCombinationName, setActiveCombinationName] = useState<string | null>(null);

  const { stageStatus, formulation, waterProfile } = useAppSelector(state => state.workflow);
  const currentStageStatus = stageStatus[WorkflowStage.SALT_MANAGEMENT];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSaltSelection = useCallback((salts: any[]) => {
    setSelectedSalts(salts);
    setSelectedIds(salts.map((s) => s.id));

    // Update workflow state (draft only — Stage 3 completes on Confirm)
    dispatch(setSaltSelection({
      selectedSalts: salts.map((salt) => ({
        id: salt.id,
        name: salt.name,
        formula: salt.formula || '',
        elements: (salt.elements || [])
          .map((el: any) => ({
            symbol: el?.element?.symbol || el?.symbol || el?.elementSymbol || '',
            percentage: Number(el?.percentage ?? el?.percent ?? 0),
          }))
          .filter((el: any) => el.symbol && el.percentage > 0),
        purity: (salt as any).purity ?? 1,
        solubility: (salt as any).solubility,
        density: (salt as any).density,
        isLiquid: Boolean((salt as any).isLiquid),
        compatibility: 'neutral' as const,
      })),
      customSalts: [],
      compatibilityMatrix: {},
    }));

    dispatch(setStageValidation({
      stage: WorkflowStage.SALT_MANAGEMENT,
      validated: salts.length > 0,
      errors: salts.length === 0 ? ['At least one salt must be selected'] : undefined
    }));
  }, [dispatch]);

  const handleSelectedIdsChange = useCallback((ids: string[]) => {
    setSelectedIds(ids);
    // Clear combination badge when user manually changes selection
    setActiveCombinationName(null);
  }, []);

  const handleLoadCombination = useCallback((saltIds: string[], combo: SaltCombination) => {
    setSelectedIds(saltIds);
    setActiveCombinationName(combo.name);
    // SaltLibraryReview controlled effect will emit full salt objects via onSaltSelection
  }, []);

  const handleCustomSaltCreated = useCallback(() => {
    // Trigger refresh of salt library
    setRefreshTrigger(prev => prev + 1);
    // Switch to library review tab to see the new salt
    setCurrentTab(0);
  }, []);

  const handleProceedToStockSolutions = useCallback(() => {
    if (selectedSalts.length === 0) {
      console.warn('Proceed blocked: no selected salts');
      return;
    }

    // Flatten + confirm Stage 3 (unlocks Stage 4 only here)
    const payload = {
      selectedSalts: selectedSalts.map((salt) => ({
        id: salt.id,
        name: salt.name,
        formula: salt.formula || '',
        elements: (salt.elements || [])
          .map((el: any) => ({
            symbol: el?.element?.symbol || el?.symbol || el?.elementSymbol || '',
            percentage: Number(el?.percentage ?? el?.percent ?? 0),
          }))
          .filter((el: any) => el.symbol && el.percentage > 0),
        purity: (salt as any).purity ?? 1,
        solubility: (salt as any).solubility,
        density: (salt as any).density,
        isLiquid: Boolean((salt as any).isLiquid),
        compatibility: 'neutral' as const,
      })),
      customSalts: [] as any[],
      compatibilityMatrix: {},
    };

    // Confirm is sync; unlock Stage 4 before route change
    dispatch(confirmSaltSelection(payload));
    // Defer navigation one tick so WorkflowLayout reads updated canAdvance/completed
    window.setTimeout(() => {
      navigate('/stock-solutions');
    }, 0);
  }, [selectedSalts, dispatch, navigate]);

  const handleBackToFormulation = () => {
    navigate('/formulation');
  };

  // Enable when we have complete selected salts (don't rely only on Redux validated lag)
  const canProceed = selectedSalts.length > 0;

  return (
    <WorkflowLayout
      stage={WorkflowStage.SALT_MANAGEMENT}
      title="Salts"
      description="Select fertilizer salts (and optional named combinations) for the matrix solve."
    >
      {formulation && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Crop: {formulation.name}
          {formulation.elements?.length
            ? ` · ${formulation.elements.length} targets`
            : ''}
          {activeCombinationName ? ` · Set: ${activeCombinationName}` : ''}
        </Typography>
      )}

      {/* Named combinations — save / load / edit */}
      <SaltCombinationsPanel
        selectedSalts={selectedSalts}
        onLoadCombination={handleLoadCombination}
      />

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="salt management tabs"
          variant="fullWidth"
        >
          <Tab
            label="Salt Library"
            icon={<ScienceIcon />}
            {...a11yProps(0)}
          />
          <Tab
            label="Create Custom Salt"
            icon={<NextIcon />}
            {...a11yProps(1)}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={currentTab} index={0}>
        <SaltLibraryReview
          onSaltSelection={handleSaltSelection}
          refreshTrigger={refreshTrigger}
          selectedIds={selectedIds}
          onSelectedIdsChange={handleSelectedIdsChange}
        />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <CustomSaltCreation
          onSaltCreated={handleCustomSaltCreated}
        />
      </TabPanel>

      {/* Selection Analysis and Confirmation */}
      {selectedSalts.length > 0 && (
        <SelectionAnalysisPanel
          selectedSalts={selectedSalts}
          formulation={formulation as any || undefined}
          onConfirmSelection={handleProceedToStockSolutions}
          onBackToFormulation={handleBackToFormulation}
          canProceed={canProceed}
        />
      )}
    </WorkflowLayout>
  );
};

export default SaltLibraryWorkflowPage;
